import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useSonner } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { CustomerImpersonationWarning } from '../../../web/components/CustomerImpersonationWarning';
import { TwilioContextProvider } from '../../components/ai/context';
import { SalesAi } from '../../components/ai/SalesAi';
import { Header } from '../../components/Header';
import { IncomingCalls } from '../../components/IncomingCalls';
import { MemeModal } from '../../components/MemeModal';
import { IMrryMessage, MrryDirection, MrryMessageType, MrryModal } from '../../components/MrryModal';
import { SlotMachineModalProvider } from '../../components/slotMachines/SlotMachineModalProvider';
import { AudioPlayerProvider } from '../../contexts/audioPlayer';
import { useSearch } from '../../contexts/search';
import { useTelephony } from '../../contexts/telephony';
import { useTysonMode } from '../../contexts/tysonMode';
import { BodyContainer } from './components/BodyContainer';
import { styleSheet } from './styles';

export type HeaderSize = 'short' | 'tall';

const MainContainerBase = ({
	children,
	bodyContainerStyles,
}: {
	children?: React.ReactNode;
	bodyContainerStyles?: StyleDeclarationValue[];
}) => {
	const location = useLocation();
	const { phoneCalls } = useTelephony();
	const { setShowSearch } = useSearch();
	const userSession = useUserSession();
	const { toasts } = useSonner();
	const { isActive: isTysonModeActive } = useTysonMode();

	const [mrryMessage, setMrryMessage] = useState<IMrryMessage>(null);
	const [showIncomingCalls, setShowIncomingCalls] = useState(false);
	const [headerHeight, setHeaderHeight] = useState<HeaderSize>('tall');

	useEffect(() => {
		if (toasts.length > 0) {
			const toast = toasts[toasts.length - 1];
			const { title: message, type } = toast;
			setMrryMessage({
				content: typeof message === 'function' ? message() : message,
				type:
					type === 'success'
						? MrryMessageType.Success
						: type === 'error'
							? MrryMessageType.Error
							: MrryMessageType.Error,
			});
		} else {
			setMrryMessage(null);
		}
	}, [toasts]);

	useEffect(() => {
		const onGlobalKeydown = (e: KeyboardEvent) => {
			if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
				e.preventDefault();
				setShowSearch(value => !value);
			}
		};
		window.addEventListener('keydown', onGlobalKeydown);
		return () => {
			window.removeEventListener('keydown', onGlobalKeydown);
		};
	}, [setShowSearch]);

	useEffect(() => {
		if (phoneCalls.incomingCalls.length) {
			setShowIncomingCalls(true);
		} else {
			setShowIncomingCalls(false);
			phoneCalls.clearIncomingCalls();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneCalls.incomingCalls.length]);

	useEffect(() => {
		setHeaderHeight(
			!!location?.pathname &&
				!location?.pathname.includes('settings') &&
				!location?.pathname.includes('reporting') &&
				!location?.pathname.includes('leaderboard')
				? 'tall'
				: 'short'
		);
	}, [location?.pathname]);

	const onIncomingCallsClose = () => {
		setShowIncomingCalls(false);
		phoneCalls.clearIncomingCalls();
	};

	return (
		<AudioPlayerProvider>
			<SlotMachineModalProvider>
				<TwilioContextProvider>
					<div className={css(styleSheet.container, isTysonModeActive && styleSheet.tysonBackground)}>
						<Header />
						<BodyContainer headerHeight={headerHeight} styles={bodyContainerStyles}>
							<CustomerImpersonationWarning styles={[styleSheet.impersonating]} userSession={userSession} />
							{children}
						</BodyContainer>
						{!!mrryMessage && <MrryModal fromDirection={MrryDirection.FromTop} message={mrryMessage} />}
						{!!showIncomingCalls && (
							<MrryModal
								fromDirection={MrryDirection.FromTop}
								message={{
									content: <IncomingCalls />,
									type: MrryMessageType.Success,
								}}
								onRequestClose={onIncomingCallsClose}
							/>
						)}
						<MemeModal />
						{userSession.account?.features?.aida?.enabled !== true && (
							<div className={css(styleSheet.noCoffee)}>
								This account does not have Coffee enabled. Please double check you are logged into the correct account.
							</div>
						)}

						{userSession.account?.features?.aida?.coachingEnabled ? <SalesAi /> : null}
					</div>
				</TwilioContextProvider>
			</SlotMachineModalProvider>
		</AudioPlayerProvider>
	);
};

export const MainContainer = observer(MainContainerBase);
